import { VideoCameraIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@clerk/clerk-react";
import { UserButton } from "@clerk/clerk-react";
import { useState, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

const apiBaseUrl = import.meta.env.VITE_API_URL;

export default function Connect() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    // Check for error query parameters
    const error = searchParams?.error;
    const message = searchParams?.error_message;

    if (error) {
      setErrorCode(error);
      setErrorMessage(message || "An error occurred during authentication");

      // Clear the error parameters from the URL
      navigate({ search: {} }, { replace: true });
    }
  }, [searchParams, navigate]);

  const onConnectClick = async () => {
    const token = await getToken();
    window.location.href = `${apiBaseUrl}/auth/google/?token=${token}`;
  };

  const dismissError = () => {
    setErrorMessage(null);
    setErrorCode(null);
  };

  const getErrorDetails = () => {
    switch (errorCode) {
      case "no_channel_found":
        return {
          title: "No YouTube Channel Found",
          suggestions: [
            "Make sure you're signing in with a Google account that has a YouTube channel",
            "Create a YouTube channel for your Google account if you don't have one",
            "Try signing in with a different Google account"
          ]
        };
      case "channel_mismatch":
        return {
          title: "Wrong YouTube Channel",
          suggestions: [
            "If you want to switch channels, please remove your existing channel from settings first",
            "Make sure you're signing in with the correct Google account"
          ]
        };
      default:
        return {
          title: "Authentication Error",
          suggestions: [
            "Please try connecting your channel again",
            "If the problem persists, contact support"
          ]
        };
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          {errorMessage && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {getErrorDetails().title}
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    <p className="mb-3">{errorMessage}</p>
                    {getErrorDetails().suggestions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-700">
                        <p className="font-medium mb-1">What to do:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {getErrorDetails().suggestions.map((suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-red-50 dark:bg-red-900/20 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 focus:outline-none"
                    onClick={dismissError}
                  >
                    <span className="sr-only">Dismiss</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <button
            type="button"
            className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 dark:border-white/15 dark:hover:border-white/25 dark:focus:outline-indigo-500 cursor-pointer"
            onClick={onConnectClick}
          >
            <VideoCameraIcon
              className="mx-auto h-12 w-12 text-gray-400 dark:text-white/50"
              aria-hidden="true"
            />
            <span className="mt-2 block text-sm font-semibold text-gray-900 dark:text-white">
              Connect your YouTube channel
            </span>
          </button>
        </div>
      </div>
      <div className="absolute bottom-4 left-4">
        <UserButton />
      </div>
    </div>
  );
}
